import { rewordValidationMessages } from '#api/errors.ts';
import { del, get, getList, post, put } from '#api/fetch.ts';
import type { ResourceListOptions } from '#dn-types/api.ts';
import type { Route, RouteListItem } from '#dn-types/route.ts';

export const DEFAULT_ROUTES_PAGE_SIZE = 25;

// GET /routes
export type GetRoutesResult = RouteListItem[];
type GetRoutesVariables = ResourceListOptions;
export function getRoutes({ cursor, pageSize = DEFAULT_ROUTES_PAGE_SIZE, init }: GetRoutesVariables = {}) {
  const params = new URLSearchParams({
    includeCounts: 'true',
    pageSize: pageSize.toString(),
  });

  if (cursor) {
    params.append('cursor', cursor);
  }

  return getList<GetRoutesResult>(`/v1/routes?${params.toString()}`, init);
}

// GET /routes/:id
export type GetRouteResult = Route;
export function getRoute(routeId: string, init?: RequestInit) {
  return get<GetRouteResult>(`/v1/routes/${encodeURIComponent(routeId)}`, init);
}

// POST /routes
export type AddRouteVariables = Omit<Route, 'id' | 'createdAt' | 'modifiedAt'>;
export type AddRouteResult = Route;
export function addRoute(formData: AddRouteVariables) {
  return (
    post<Route>('/v1/routes', formData)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'You already have a route with this name.';
          }
          if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
            if (typeof details.extra?.limit === 'number') {
              return `You have reached the limit of ${details.extra.limit} routes. Delete some unused routes before creating more.`;
            }
            return 'You have the maximum number of routes allowed. Delete some unused routes before creating more.';
          }
        });
      })
  );
}

// PUT /routes/:id
export type EditRouteVariables = {
  routeId: `route-${string}`;
  variables: Omit<Route, 'id' | 'createdAt' | 'modifiedAt'>;
};
export type EditRouteResult = Route;
export function editRoute(input: EditRouteVariables) {
  return (
    put<Route>(`/v1/routes/${encodeURIComponent(input.routeId)}`, input.variables)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === 'name' && details.code === 'ERR_DUPLICATE_VALUE') {
            return 'You already have a route with this name.';
          }
          if (details.path === undefined && details.code === 'ERR_LIMIT_EXCEEDED') {
            if (typeof details.extra?.limit === 'number') {
              return `You have reached the limit of ${details.extra.limit} tags. Delete some unused tags before creating more.`;
            }
            return 'You have the maximum number of tags allowed. Delete some unused tags before creating more.';
          }
        });
      })
  );
}

// DELETE /routes/:id
export type DeleteRouteResult = Record<string, never>;
export function deleteRoute(routeId: string) {
  return (
    del<DeleteRouteResult>(`/v1/routes/${encodeURIComponent(routeId)}`)
      // Convert from server error codes to more friendly strings, if possible
      .catch((err: unknown) => {
        rewordValidationMessages(err, (details) => {
          if (details.path === undefined && details.code === 'ERR_HAS_DEPENDENTS') {
            // TODO: [@jasikpark] [2024-02-15] Validate if this is the correct verbiage.
            return 'There are hosts that exist using this route, please remove the hosts first.';
          }
        });
      })
  );
}
